import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { RMSIconButton } from '../../components/buttons/index';
import AddIcon from '@material-ui/icons/Add';
import InfoIcon from '@material-ui/icons/Info';
import { notify } from 'reducers/NotifierReducer';
import { useDispatch } from 'react-redux';
import { RMSAutocomplete } from '../../../shared/components/form-controls/index';
import generalServices, { getPlaceById } from '../../services/generalServices';
import { executeApiAction } from '../../utility/apiHelper';
import { isEmpty } from 'features/shared/utility/helper';
import { useFormContext } from 'react-hook-form';
import { usePlaceDialog } from '../../../user/hooks/usePlaceDialog';

const SearchPlaceAutoComplete = ({
  name,
  label,
  onSelectionChange,
  onPlaceCreated,
  onPlaceUpdated,
  placeholder,
  defaultPlaceId = null,
  defaultPlaceSelectedCallback = null,
  showAddIcon = true,
  onAddIconClick,
  showDetailsIcon = true,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const { setValue } = useFormContext();
  const { openPlaceDialog } = usePlaceDialog();
  const isMounted = useRef(true);

  const placePerms = usePermissions(PERMISSION_RESOURCES.PLACE);
  const canCreatePlace = placePerms.Create;
  const canViewPlaceDetails = Boolean(placePerms.Read || placePerms.Edit);

  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSavedPlace = (place, callback) => {
    if (!isMounted.current || !place?.ptsPlaceID) return;
    setPlaces((previous) => [
      place,
      ...previous.filter((option) => option.ptsPlaceID !== place.ptsPlaceID),
    ]);
    setValue(name, place.ptsPlaceID, { shouldDirty: true, shouldValidate: true });
    callback?.(place);
  };

  const handleAddIconClick = (event) => {
    if (disabled) return;
    if (onAddIconClick) {
      onAddIconClick(event);
      return;
    }
    openPlaceDialog(null, {
      closeOnSave: true,
      onPlaceCreated: (place) => handleSavedPlace(place, onPlaceCreated || onSelectionChange),
    });
  };

  const searchPlaces = async (searchText) => {
    await executeApiAction({
      setLoading,
      action: async () => {
        return generalServices.getPlacesBySearch(searchText);
      },
      onSuccess: (response) => setPlaces(response),
      onError: (error) => {
        dispatch(notify(error.message, 'error'));
      },
    });
  };

  const handleDefaultPlaceId = useCallback(
    async (placeId) => {
      if (placeId) {
        await executeApiAction({
          setLoading,
          dispatch: dispatch,
          action: async () => {
            return getPlaceById(placeId);
          },
          onSuccess: (response) => {
            setPlaces([response]);
            defaultPlaceSelectedCallback?.(response);
          },
        });
      }
    },
    [defaultPlaceSelectedCallback, dispatch]
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isEmpty(defaultPlaceId)) return;
    handleDefaultPlaceId(defaultPlaceId);
  }, [defaultPlaceId, handleDefaultPlaceId]);

  return (
    <>
      <RMSAutocomplete
        name={name}
        label={label ?? 'Place'}
        placeholder={placeholder}
        getOptionLabel={(option) => `${option.PlaceName}`}
        optionLabel="PlaceName"
        optionValue="ptsPlaceID"
        options={places}
        leftContent={() =>
          showAddIcon &&
          canCreatePlace && (
            <RMSIconButton
              icon={<AddIcon fontSize="small" />}
              disabled={disabled}
              tooltip="Add Place"
              onClick={(event) => {
                event.stopPropagation();
                handleAddIconClick(event);
              }}
              size="small"
            />
          )
        }
        rightContent={({ value }) =>
          showDetailsIcon &&
          value &&
          canViewPlaceDetails && (
            <RMSIconButton
              icon={<InfoIcon />}
              tooltip="Place Details"
              size="small"
              disabled={disabled}
              onClick={() =>
                openPlaceDialog(value.ptsPlaceID, {
                  closeOnSave: true,
                  onPlaceUpdated: (place) =>
                    handleSavedPlace(place, onPlaceUpdated || onSelectionChange),
                })
              }
            />
          )
        }
        onSelectionChange={(data) => onSelectionChange && onSelectionChange(data)}
        onSearch={searchPlaces}
        disabled={disabled}
        loading={loading}
      />
    </>
  );
};

export default SearchPlaceAutoComplete;
